import {
  CircularProgress,
  IconButton,
  makeStyles,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from "@material-ui/core"
import clsx from "clsx"
import dayjs from "dayjs"
import React, { SyntheticEvent, useEffect, useState } from "react"
import {
  ClientFragment,
  useNudgesScheduleByUserQuery,
  useQuestionnairesScheduleByUserQuery,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { isNudgeSchedule } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { ScheduleItem } from "../../utils/types"
import Button, { LiteButton } from "../atoms/button/Button"
import ChevronLeftLargeIcon from "../atoms/ChevronLeftLargeIcon"
import ChevronRightIcon from "../atoms/ChevronRightIcon"
import DeleteScheduleDialog from "../molecules/DeleteScheduleDialog"
import DisabledActionTooltip from "../molecules/DisabledActionTooltip"
import ClientLocalTimeDisplay from "../molecules/ClientLocalTimeDisplay"
import ScheduledItemCard from "../molecules/ScheduledItemCard"
import ScheduleItemModal from "./ScheduleItemModal"
import { Alert, Color } from "@material-ui/lab"

const SCHEDULING_DISABLED_TOOLTIP =
  "This action is disabled because the client is disconnected."

interface ScheduleDate {
  date: string
  items: ScheduleItem[]
}

interface ScheduleOverviewProps {
  client: ClientFragment
  canSchedule: boolean
}

export default function ScheduleOverview({
  client,
  canSchedule,
}: ScheduleOverviewProps) {
  const classes = useStyles()
  const startDate = dayjs().subtract(dayjs().day(), "days")
  const endDate = startDate.add(6, "days")
  const [currentStartDate, setCurrentStartDate] =
    useState<dayjs.Dayjs>(startDate)
  const [currentEndDate, setCurrentEndDate] = useState<dayjs.Dayjs>(endDate)
  const [onCurrentWeek, setOnCurrentWeek] = useState<boolean>(true)
  const [schedule, setSchedule] = useState<ScheduleDate[] | undefined>(
    undefined
  )
  const [currentItem, setCurrentItem] = useState<ScheduleItem | undefined>(
    undefined
  )
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [openScheduleDialog, setOpenScheduleDialog] = useState<boolean>(false)
  const [currentDate, setCurrentDate] = useState<dayjs.Dayjs>()
  const graphqlClient = useGraphQLClient()

  const { data: nudgesData, isLoading: loadingNudges } =
    useNudgesScheduleByUserQuery(graphqlClient, {
      user_id: client.id,
    })
  const { data: questionnairesData, isLoading: loadingQuestionnaires } =
    useQuestionnairesScheduleByUserQuery(graphqlClient, {
      user_id: client.id,
    })
  const rawSchedule = React.useMemo(
    () => [
      ...(nudgesData?.nudge_schedules ?? []),
      ...(questionnairesData?.questionnaire_schedules ?? []),
    ],
    [nudgesData, questionnairesData]
  )

  useEffect(() => {
    const nonRecurringItems = rawSchedule.filter((item) => item.one_time)
    const mapSchedule: Map<string, ScheduleItem[]> = new Map()
    nonRecurringItems.forEach((item) => {
      const date = dayjs(item.scheduled_at).format("YYYY-MM-DD")
      if (!mapSchedule.has(date)) {
        mapSchedule.set(date, [])
      }
      const dateSchedule = mapSchedule.get(date) ?? []
      dateSchedule.push(item)
      mapSchedule.set(date, dateSchedule)
    })
    const newSchedule = Array.from(mapSchedule.keys()).map((date) => ({
      date,
      items: mapSchedule.get(date) ?? [],
    }))
    setSchedule(newSchedule)
  }, [rawSchedule])

  const filteredSchedule = (schedule ?? []).filter(
    (item) =>
      dayjs(item.date).isAfter(currentStartDate.subtract(1, "day"), "day") &&
      dayjs(item.date).isBefore(currentEndDate.add(1, "day"), "day")
  )

  const recurringItems = rawSchedule.filter((item) => !item.one_time)
  const currentSchedule: ScheduleDate[] = []
  let noItemsForWeek = true
  for (let i = 0; i < 7; i++) {
    const date = currentStartDate.add(i, "days").format("YYYY-MM-DD")
    const dayOfWeek = dayjs(date).day()
    const scheduleDate = {
      date,
      items: filteredSchedule.find((item) => item.date === date)?.items ?? [],
    }
    const recurring = recurringItems
      .filter((item) => {
        // Don't show recurring items before their start_date
        const startAt = dayjs(item.scheduled_at)
        if (
          startAt.isAfter(dayjs(date), "day") &&
          !startAt.isSame(date, "day")
        ) {
          return false
        }
        // Don't show recurring items if scheduled for today, but before now
        if (
          date === dayjs().format("YYYY-MM-DD") &&
          startAt.hour() * 60 + startAt.minute() <
            dayjs().hour() * 60 + dayjs().minute() &&
          (isNudgeSchedule(item)
            ? item.nudge_takes.length === 0
            : item.questionnaire_takes.length === 0)
        ) {
          return false
        }
        switch (dayOfWeek) {
          case 0:
            return item.recurring_sunday
          case 1:
            return item.recurring_monday
          case 2:
            return item.recurring_tuesday
          case 3:
            return item.recurring_wednesday
          case 4:
            return item.recurring_thursday
          case 5:
            return item.recurring_friday
          case 6:
            return item.recurring_saturday
        }
        throw new Error(
          "Trying to organize recurring item with no recurring days set"
        )
      })
      // filter out blackout days
      .filter(
        (item) =>
          !item.schedule_blackout_dates.find((d) => {
            return (
              dayjs(d.blackout_date).isSame(dayjs(date), "day") ||
              (d.blackout_following && dayjs(date).isAfter(d.blackout_date))
            )
          })
      )
    scheduleDate.items = scheduleDate.items.concat(recurring)
    scheduleDate.items.sort((a, b) => {
      const aTime =
        dayjs(a.scheduled_at).hour() * 60 + dayjs(a.scheduled_at).minute()
      const bTime =
        dayjs(b.scheduled_at).hour() * 60 + dayjs(b.scheduled_at).minute()
      return aTime < bTime ? -1 : aTime > bTime ? 1 : 0
    })
    if (scheduleDate.items.length > 0) {
      noItemsForWeek = false
    }
    currentSchedule.push(scheduleDate)
  }

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: Color | undefined
    message: string | undefined
  }>({
    open: false,
    severity: undefined,
    message: undefined,
  })

  const handleCloseSnackbar = (
    event: SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }

  const doesNotHavePermissions = (check: boolean) => {
    if (check) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Oops! Something went wrong. Please try again later.",
      })
    }
  }

  return (
    <>
      <div className={classes.scheduleContainer}>
        {client.most_recent_timezone && (
          <ClientLocalTimeDisplay client={client} />
        )}
        <div className={classes.scheduleHeader}>
          <div className={classes.chevronMonths}>
            <div className={classes.chevrons}>
              <IconButton
                className={clsx(classes.chevronButton, classes.leftChevron)}
                onClick={() => {
                  const newEndDate = currentStartDate.subtract(1, "days")
                  const newStartDate = newEndDate.subtract(6, "days")
                  setCurrentStartDate(newStartDate)
                  setCurrentEndDate(newEndDate)
                  setOnCurrentWeek(
                    newStartDate.format("MMM DD") === startDate.format("MMM DD")
                  )
                }}
              >
                <ChevronLeftLargeIcon />
              </IconButton>
              <IconButton
                className={classes.chevronButton}
                onClick={() => {
                  const newStartDate = currentEndDate.add(1, "days")
                  const newEndDate = newStartDate.add(6, "days")
                  setCurrentStartDate(newStartDate)
                  setCurrentEndDate(newEndDate)
                  setOnCurrentWeek(
                    newStartDate.format("MMM DD") === startDate.format("MMM DD")
                  )
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </div>
            <Typography variant="h3" className={classes.months}>
              {`${currentStartDate.format("MMM DD")}-` +
                `${currentEndDate.format("MMM DD")}`}
            </Typography>
          </div>
          <div className={classes.headerRight}>
            {!onCurrentWeek && (
              <LiteButton
                onClick={() => {
                  setCurrentStartDate(startDate)
                  setCurrentEndDate(endDate)
                  setOnCurrentWeek(true)
                }}
              >
                <Typography variant="body1" className={classes.buttonText}>
                  Return to this Week
                </Typography>
              </LiteButton>
            )}
          </div>
        </div>
        {currentSchedule && (
          <>
            {!noItemsForWeek ? (
              <>
                {currentSchedule.map((scheduleDate) => (
                  <div className={classes.scheduleDay} key={scheduleDate.date}>
                    <Typography variant="h6" className={classes.scheduleDate}>
                      {dayjs(scheduleDate.date).format("dddd, MMMM DD")}
                    </Typography>
                    {scheduleDate.items.length > 0 ? (
                      <div className={classes.items}>
                        {scheduleDate.items.map((scheduleItem) => {
                          return (
                            <ScheduledItemCard
                              key={scheduleItem.id}
                              scheduleDate={scheduleDate.date}
                              scheduleItem={scheduleItem}
                              canSchedule={canSchedule}
                              setCurrentItem={setCurrentItem}
                              setOpenScheduleDialog={setOpenScheduleDialog}
                              setOpenDeleteDialog={setOpenDeleteDialog}
                              setCurrentDate={setCurrentDate}
                            />
                          )
                        })}
                      </div>
                    ) : (
                      <div className={classes.noItemsScheduled}>
                        <Typography
                          variant="body1"
                          className={classes.noItemsScheduledText}
                        >
                          No items scheduled
                        </Typography>
                      </div>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <div className={classes.noScheduleBody}>
                {loadingNudges && loadingQuestionnaires ? (
                  <CircularProgress />
                ) : (
                  <>
                    <Typography variant="h6" className={classes.noScheduleText}>
                      There are no nudges or questionnaires scheduled this week
                    </Typography>
                    <DisabledActionTooltip
                      disabled={!canSchedule}
                      text={SCHEDULING_DISABLED_TOOLTIP}
                    >
                      <Button
                        customVariant="secondary"
                        className={classes.addButton}
                        disabled={!canSchedule}
                        onClick={() => {
                          setCurrentItem(undefined)
                          setOpenScheduleDialog(true)
                        }}
                      >
                        Add to Schedule
                      </Button>
                    </DisabledActionTooltip>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ScheduleItemModal
        client={client}
        open={openScheduleDialog}
        onClose={() => {
          setCurrentItem(undefined)
          setOpenScheduleDialog(false)
        }}
        item={openScheduleDialog ? currentItem : undefined}
        canSchedule={canSchedule}
      />

      {currentItem && (
        <DeleteScheduleDialog
          open={openDeleteDialog}
          onClose={() => {
            setCurrentItem(undefined)
            setOpenDeleteDialog(false)
            setCurrentDate(undefined)
          }}
          item={currentItem}
          currentDate={currentDate}
          insufficientPermissions={doesNotHavePermissions}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  scheduleContainer: {
    display: "flex",
    flexDirection: "column",
    height: "fit-content",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  noScheduleBody: {
    display: "grid",
    gap: theme.spacing(4),
    justifyItems: "center",
    alignContent: "center",
    minHeight: 380,
    backgroundColor: colors.primary.cloud,
    borderRadius: 4,
    padding: theme.spacing(1),
  },
  noScheduleText: {
    fontStyle: "italic",
    fontWeight: "normal",
    textAlign: "center",
    maxWidth: 350,
  },
  addButton: {
    "& .MuiButton-label": {
      padding: theme.spacing(0, 5),
      lineHeight: "normal",
    },
  },
  months: {
    color: colors.neutral.denim,
  },
  chevronButton: {
    margin: 0,
    border: 0,
    padding: 0,
    height: 32,
    width: 32,
  },
  scheduleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 52,
    marginBottom: theme.spacing(2),
  },
  leftChevron: {
    marginRight: theme.spacing(1),
  },
  chevrons: {
    display: "flex",
    alignItems: "center",
    marginRight: 4,
  },
  chevronMonths: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  buttonText: {
    color: "inherit",
  },
  scheduleDate: {
    color: colors.primary.midnight,
    marginBottom: 12,
  },
  itemDate: {
    color: colors.neutral.denim,
    fontWeight: 400,
    marginRight: theme.spacing(1),
  },
  itemTitle: {
    color: colors.neutral.denim,
    fontWeight: 700,
  },
  itemMessage: {
    color: colors.neutral.denim,
    fontWeight: 400,
    marginLeft: 0,
    [theme.breakpoints.up("sm")]: {
      marginLeft: 64,
    },
    overflowWrap: "anywhere",
  },
  invalidItemMessage: {
    textDecorationLine: "line-through",
    color: colors.neutral.denim,
    fontWeight: 400,
    marginLeft: 0,
    [theme.breakpoints.up("sm")]: {
      marginLeft: 64,
    },
    overflowWrap: "anywhere",
  },
  itemContent: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  itemDateTitle: {
    display: "flex",
    alignItems: "start",
    flexWrap: "wrap",
    minWidth: 0,
  },
  items: {
    display: "grid",
    gap: 12,
    minWidth: 0,
  },
  scheduleDay: {
    marginBottom: theme.spacing(2),
    minWidth: 0,
  },
  noItemsScheduled: {
    width: "100%",
    height: 33,
    display: "flex",
    alignItems: "center",
    backgroundColor: colors.primary.cloud_05,
    borderRadius: 4,
    marginTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    boxSizing: "border-box",
  },
  noItemsScheduledText: {
    textStyle: "italic",
    color: colors.neutral.denim,
  },
  menuPaper: {
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: colors.neutral.white,
    boxShadow:
      "0px 0px 4px rgba(54, 53, 69, 0.1), 0px 3px 12px rgba(54, 53, 69, 0.05)",
    width: 150,
  },
}))
